import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Layers, Plus, Pencil, Trash2, Check, ChevronDown, Search, ArrowUpDown, X, AlertCircle, AlertTriangle, Image as ImageIcon, Upload, Minus } from 'lucide-react';
import { API_URL } from '../config';
import Navbar from './Navbar';

const ZoneManagement = ({ token, role, username, setToken, permissions = [] }) => {
    const [zones, setZones] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingZone, setEditingZone] = useState(null);
    const [newZone, setNewZone] = useState({
        zone_name: '',
        display_order: 0,
        is_active: true,
        image_url: ''
    });
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'display_order', direction: 'asc' });
    const [previewZone, setPreviewZone] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isRowsDropdownOpen, setIsRowsDropdownOpen] = useState(false);
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const fileInputRef = useRef(null);

    // Track original image and newly uploaded images in current session
    const [originalImage, setOriginalImage] = useState('');
    const [sessionUploadedImages, setSessionUploadedImages] = useState([]);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-20" />;
        return sortConfig.direction === 'asc' ?
            <ChevronDown className="w-3 h-3 ml-1 text-indigo-600 rotate-180" /> :
            <ChevronDown className="w-3 h-3 ml-1 text-indigo-600" />;
    };

    const fetchZones = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/zones`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setZones(res.data);
        } catch (err) {
            console.error(err);
        }
    }, [token]);

    useEffect(() => {
        fetchZones();
    }, [fetchZones]);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setError('Please upload an image file.');
            return;
        }

        const formData = new FormData();
        formData.append('image', file);

        try {
            setUploading(true);
            setError(null);

            const res = await axios.post(`${API_URL}/api/upload/zone`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            const newUrl = res.data.url;
            setNewZone({ ...newZone, image_url: newUrl });
            setSessionUploadedImages(prev => [...prev, newUrl]);

            // Reset input value to allow re-uploading the same file if deleted
            if (e.target) e.target.value = '';
        } catch (err) {
            console.error('Upload Error:', err);
            setError(err.response?.data?.message || 'Failed to upload image.');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteImage = () => {
        setNewZone({ ...newZone, image_url: '' });
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSave = async (e) => {
        e.preventDefault();

        // Frontend Validation
        if (!newZone.zone_name.trim()) {
            setError('กรุณาระบุชื่อโซน');
            return;
        }

        setError('');
        try {
            const token = localStorage.getItem('token');
            if (editingZone) {
                await axios.put(`${API_URL}/api/zones/${editingZone.id}`, newZone, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccessMessage('อัปเดตข้อมูลโซนเรียบร้อยแล้ว');
            } else {
                await axios.post(`${API_URL}/api/zones`, newZone, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccessMessage('เพิ่มโซนใหม่เรียบร้อยแล้ว');
            }

            // Cleanup images on SUCCESSFUL save
            // 1. If original image was changed/removed, delete it
            if (originalImage && originalImage !== newZone.image_url) {
                try {
                    await axios.post(`${API_URL}/api/delete/image`, { url: originalImage }, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                } catch (err) { console.error('Failed to cleanup original image:', err); }
            }

            // 2. Delete all intermediate session uploads (those NOT in final image_url)
            const imagesToDelete = sessionUploadedImages.filter(img => img !== newZone.image_url);
            for (const imgUrl of imagesToDelete) {
                try {
                    await axios.post(`${API_URL}/api/delete/image`, { url: imgUrl }, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                } catch (err) { console.error('Failed to cleanup intermediate session image:', err); }
            }

            // Mark session as saved so closeModal doesn't delete the final image
            setSessionUploadedImages([]);

            setShowSuccessPopup(true);
            setIsModalOpen(false);
            fetchZones();
        } catch (err) {
            console.error('Save Zone Error:', err);
            const backendMsg = err.response?.data?.message || (typeof err.response?.data === 'string' ? err.response.data : null);
            setError(backendMsg || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
        }
    };

    const handleDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            // 1. Get zone details first to see if it has an image
            const zoneToDelete = zones.find(z => z.id === deleteTargetId);

            // 2. Delete the zone from database
            await axios.delete(`${API_URL}/api/zones/${deleteTargetId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // 3. If zone had an image, delete it from folder
            if (zoneToDelete && zoneToDelete.image_url) {
                try {
                    await axios.post(`${API_URL}/api/delete/image`, { url: zoneToDelete.image_url }, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                } catch (imgErr) {
                    console.error('Failed to delete image file during zone deletion:', imgErr);
                }
            }

            setSuccessMessage('ลบข้อมูลโซนเรียบร้อยแล้ว');
            setShowSuccessPopup(true);
            setShowDeleteConfirm(false);
            fetchZones();
        } catch (err) {
            console.error('Delete Error:', err);
            const msg = err.response?.data?.message || err.response?.data?.error || 'เกิดข้อผิดพลาดในการลบข้อมูล';
            setErrorMessage(msg);
            setShowErrorPopup(true);
            setShowDeleteConfirm(false);
        }
    };

    const openModal = (z = null) => {
        if (z) {
            setEditingZone(z);
            setNewZone({
                zone_name: z.zone_name,
                display_order: z.display_order,
                is_active: z.is_active === 1 || z.is_active === true,
                image_url: z.image_url || ''
            });
            setOriginalImage(z.image_url || '');
        } else {
            setEditingZone(null);
            setNewZone({ zone_name: '', display_order: zones.length + 1, is_active: true, image_url: '' });
            setOriginalImage('');
        }
        setSessionUploadedImages([]);
        setError('');
        setIsModalOpen(true);
    };

    const closeModal = async () => {
        // If user closes modal without saving, delete all images uploaded in this session
        if (sessionUploadedImages.length > 0) {
            const token = localStorage.getItem('token');
            for (const imgUrl of sessionUploadedImages) {
                try {
                    await axios.post(`${API_URL}/api/delete/image`, { url: imgUrl }, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                } catch (err) {
                    console.error('Failed to cleanup unsaved image:', imgUrl, err);
                }
            }
        }
        setIsModalOpen(false);
        setEditingZone(null);
        setSessionUploadedImages([]);
    };

    const menuPerm = permissions.find(p => p.role.toLowerCase() === role.toLowerCase() && (p.menu_key === 'zones' || p.menu_key === 'dashboard'));
    const canEdit = menuPerm ? (menuPerm.can_edit === 1 || menuPerm.can_edit === true) : false;

    const filteredZones = zones.filter(z =>
        z.zone_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sortedZones = [...filteredZones].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';

        if (sortConfig.key === 'zone_name') {
            return sortConfig.direction === 'asc'
                ? aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' })
                : bValue.localeCompare(aValue, undefined, { numeric: true, sensitivity: 'base' });
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const totalItems = sortedZones.length;
    const totalPages = Math.ceil(totalItems / rowsPerPage);
    const currentZones = sortedZones.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    // Reset page when filtering
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, rowsPerPage]);

    return (
        <div className="bg-slate-50 min-h-screen pb-10 text-slate-900">
            <Navbar role={role} username={username} onLogout={() => setToken(null)} permissions={permissions} />

            <div className="max-w-7xl mx-auto px-4 md:px-8 space-y-6">
                <div className="relative md:sticky md:top-[64px] z-40 bg-slate-50/80 pt-1.5 pb-2 mb-2 -mx-2 px-2 md:-mx-4 md:px-4 ">
                    <div className="bg-white p-3 sm:p-4 rounded-xl shadow-md border border-slate-200">
                        <div className="flex flex-wrap gap-2 sm:gap-4 items-center">
                            <div className="relative w-full md:w-64">
                                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                    <Search className="w-4 h-4 text-slate-400" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="ค้นหาโซน..."
                                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 outline-none text-sm placeholder:text-slate-400"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            <div className="ml-auto">
                                {canEdit && (
                                    <button
                                        onClick={() => openModal()}
                                        className="w-full md:w-auto flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] shadow-md shadow-blue-500/10 active:scale-95 border border-blue-500/20 "
                                    >
                                        <Plus className="w-4 h-4 shrink-0" />
                                        <span className="truncate uppercase tracking-wide">เพิ่มโซน</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                        <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                            <Layers className="w-5 h-5 text-indigo-600" />
                            Zone Management
                        </h2>
                        <span className="text-[9px] font-bold text-slate-500 bg-slate-200 px-2 py-1 rounded-full">{filteredZones.length} โซน</span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse table-fixed min-w-[1000px]">
                            <thead>
                                <tr>
                                    <th onClick={() => requestSort('display_order')} className="w-[60px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                        <div className="flex items-center justify-center gap-1">ลำดับ {getSortIcon('display_order')}</div>
                                    </th>
                                    <th onClick={() => requestSort('zone_name')} className="min-w-[150px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-left cursor-pointer hover:bg-slate-100 transition-colors">
                                        <div className="flex items-center gap-1 pl-4">ชื่อโซน {getSortIcon('zone_name')}</div>
                                    </th>
                                    <th onClick={() => requestSort('image_url')} className="w-[80px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                        <div className="flex items-center justify-center gap-1">รูปภาพ {getSortIcon('image_url')}</div>
                                    </th>
                                    <th className="w-auto"></th>
                                    <th onClick={() => requestSort('created_at')} className="w-[120px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-left cursor-pointer hover:bg-slate-100 transition-colors">
                                        <div className="flex items-center gap-1">วันที่สร้าง {getSortIcon('created_at')}</div>
                                    </th>
                                    <th onClick={() => requestSort('updated_at')} className="w-[120px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-left cursor-pointer hover:bg-slate-100 transition-colors">
                                        <div className="flex items-center gap-1">วันที่อัปเดต {getSortIcon('updated_at')}</div>
                                    </th>
                                    <th onClick={() => requestSort('is_active')} className="w-[90px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                        <div className="flex items-center justify-center gap-1">สถานะ {getSortIcon('is_active')}</div>
                                    </th>
                                    {canEdit && <th className="w-[70px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-right pr-4"></th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {currentZones.length === 0 ? (
                                    <tr>
                                        <td colSpan={canEdit ? 7 : 6} className="p-20 text-center text-slate-500">
                                            <div className="flex flex-col items-center justify-center gap-3">
                                                <div className="bg-slate-50 p-4 rounded-full">
                                                    <Layers className="w-8 h-8 text-slate-300" />
                                                </div>
                                                <p className="font-bold text-sm text-slate-400">ไม่พบข้อมูลโซน</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    currentZones.map((z) => (
                                        <tr key={z.id} className="hover:bg-slate-50/50 transition-colors group">
                                            <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                <div className="flex flex-col items-center justify-center text-center">
                                                    <span className="text-[11px] font-black text-slate-700 leading-none">{z.display_order}</span>
                                                </div>
                                            </td>
                                            <td className="px-1 py-1 align-middle pl-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[11px] font-black text-slate-700 leading-none">{z.zone_name}</span>
                                                </div>
                                            </td>
                                            <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                <div className="flex items-center justify-center">
                                                    {z.image_url ? (
                                                        <button
                                                            onClick={() => setPreviewZone(z)}
                                                            className="p-1 px-1.5 bg-blue-50 text-blue-500 rounded-md hover:bg-blue-100 transition-colors border border-blue-100"
                                                            title="ดูรูปภาพแผนที่"
                                                        >
                                                            <ImageIcon size={11} strokeWidth={2.5} />
                                                        </button>
                                                    ) : (
                                                        <span className="text-[9px] text-slate-300 font-bold italic">None</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="w-auto"></td>
                                            <td className="px-1 py-1 align-middle whitespace-nowrap text-[10px]">
                                                <div className="flex flex-col justify-center leading-none items-start">
                                                    <span className="text-[10px] font-black text-slate-700">
                                                        {z.created_at ? new Date(z.created_at).toLocaleString('th-TH', { day: 'numeric', month: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '') : '-'}
                                                    </span>
                                                    <span className="text-[8px] text-slate-400 mt-0.5">by {z.creator_name || '-'}</span>
                                                </div>
                                            </td>
                                            <td className="px-1 py-1 align-middle whitespace-nowrap text-[10px]">
                                                <div className="flex flex-col justify-center leading-none items-start">
                                                    <span className="text-[10px] font-black text-slate-700">
                                                        {z.updated_at ? new Date(z.updated_at).toLocaleString('th-TH', { day: 'numeric', month: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '') : '-'}
                                                    </span>
                                                    <span className="text-[8px] text-slate-400 mt-0.5">by {z.updater_name || '-'}</span>
                                                </div>
                                            </td>
                                            <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                <div className="flex items-center justify-center text-center">
                                                    <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold tracking-wide border ${z.is_active ? 'bg-green-50 text-green-700 border-green-200' : 'bg-rose-50 text-rose-700 border-rose-200'
                                                        }`}>
                                                        {z.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </div>
                                            </td>
                                            {canEdit && (
                                                <td className="px-1 py-1 align-middle text-right pr-4">
                                                    <div className="flex justify-end items-center gap-1.5 pr-1">
                                                        <button
                                                            onClick={() => openModal(z)}
                                                            className="text-slate-300 hover:text-blue-500 transition-colors active:scale-95"
                                                            title="แก้ไข"
                                                        >
                                                            <Pencil size={13} strokeWidth={2.5} />
                                                        </button>
                                                        <div className="w-[1px] h-2.5 bg-slate-100" />
                                                        <button
                                                            onClick={() => { setDeleteTargetId(z.id); setShowDeleteConfirm(true); }}
                                                            className="text-slate-300 hover:text-rose-500 transition-colors active:scale-95"
                                                            title="ลบ"
                                                        >
                                                            <Trash2 size={13} strokeWidth={2.5} />
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {totalItems > 0 && (
                        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/30 flex flex-col md:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">แสดงรายการ:</span>
                                <div className="relative group">
                                    <button
                                        type="button"
                                        onClick={() => setIsRowsDropdownOpen(!isRowsDropdownOpen)}
                                        className="bg-white border border-slate-200 rounded-lg px-2 py-1 min-w-[55px] flex items-center justify-between gap-1.5 shadow-sm hover:border-slate-300 hover:bg-slate-50 outline-none"
                                    >
                                        <span className="text-[12px] font-bold text-slate-700">{rowsPerPage}</span>
                                        <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${isRowsDropdownOpen ? 'rotate-180 text-blue-500' : ''}`} />
                                    </button>

                                    {isRowsDropdownOpen && (
                                        <>
                                            <div className="fixed inset-0 z-40" onClick={() => setIsRowsDropdownOpen(false)} />
                                            <div className="absolute bottom-full left-0 mb-2 min-w-full w-max bg-white border border-slate-100 rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-200">
                                                <div className="py-0.5">
                                                    {[10, 20, 50, 100].map(size => (
                                                        <button
                                                            key={size}
                                                            onClick={() => {
                                                                setRowsPerPage(size);
                                                                setIsRowsDropdownOpen(false);
                                                                setCurrentPage(1);
                                                            }}
                                                            className={`w-full px-3 py-1.5 text-[12px] font-bold flex items-center justify-between transition-colors ${rowsPerPage === size ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                                        >
                                                            <span>{size}</span>
                                                            {rowsPerPage === size && <Check className="w-3 h-3" />}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider ml-2">
                                    {(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, totalItems)} จาก {totalItems}
                                </span>
                            </div>

                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    className={`p-1.5 rounded-lg  ${currentPage === 1 ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                    title="First Page"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
                                </button>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    className={`p-1.5 rounded-lg  ${currentPage === 1 ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                    title="Previous Page"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
                                </button>

                                <div className="flex items-center gap-1 px-2">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) pageNum = i + 1;
                                        else if (currentPage <= 3) pageNum = i + 1;
                                        else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                        else pageNum = currentPage - 2 + i;

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-black  ${currentPage === pageNum ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                    className={`p-1.5 rounded-lg  ${currentPage === totalPages ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                    title="Next Page"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                                </button>
                                <button
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    className={`p-1.5 rounded-lg  ${currentPage === totalPages ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                    title="Last Page"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={closeModal}
                    />
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col z-10 animate-in zoom-in-95 fade-in duration-200 border border-slate-200 text-slate-800">
                        {/* Header */}
                        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
                            <div>
                                <h2 className="text-sm font-bold text-slate-800 tracking-tight">
                                    {editingZone ? 'แก้ไขโซน' : 'เพิ่มโซนใหม่'}
                                </h2>
                                <p className="text-xs font-medium text-slate-400 mt-0.5">ระบุชื่อโซน ลำดับการแสดงผล และแผนผังโซน</p>
                            </div>
                            <button
                                type="button"
                                onClick={closeModal}
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600  font-bold"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleSave} noValidate autoComplete="off" className="flex flex-col flex-1 overflow-hidden">
                            <div className="p-4 space-y-4 overflow-y-auto flex-1">
                                <div className="space-y-4">
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-slate-500 tracking-wider">ชื่อโซน<span className="text-rose-500">*</span></label>
                                        <input
                                            className={`w-full text-[13px] font-medium px-3 py-1.5 rounded-lg border outline-none ${error && !newZone.zone_name.trim() ? 'border-rose-500 bg-rose-50/20' : 'border-slate-200'}`}
                                            value={newZone.zone_name}
                                            onChange={(e) => {
                                                setNewZone({ ...newZone, zone_name: e.target.value });
                                                if (error) setError('');
                                            }}
                                            required
                                            placeholder="เช่น ชั้น G, ชั้น 2"
                                        />
                                    </div>

                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-slate-500 tracking-wider">แผนผังโซน</label>
                                        <div className="relative group">
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                onChange={handleImageUpload}
                                                className="hidden"
                                                accept="image/*"
                                            />

                                            {newZone.image_url ? (
                                                <div className="relative w-full aspect-video rounded-xl border-2 border-dashed border-slate-200 overflow-hidden bg-slate-50 group-hover:border-blue-200 transition-colors">
                                                    <img
                                                        src={`${API_URL}${newZone.image_url}`}
                                                        alt="Zone Preview"
                                                        className="w-full h-full object-contain"
                                                    />
                                                    <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => fileInputRef.current.click()}
                                                            className="px-3 py-1.5 bg-white text-slate-900 rounded-lg text-[10px] font-bold shadow-lg shadow-white/10 active:scale-95 "
                                                        >
                                                            เปลี่ยนรูป
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={handleDeleteImage}
                                                            className="px-3 py-1.5 bg-rose-500 text-white rounded-lg text-[10px] font-bold shadow-lg shadow-rose-500/20 active:scale-95 "
                                                        >
                                                            ลบรูป
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => fileInputRef.current.click()}
                                                    disabled={uploading}
                                                    className="w-full aspect-video flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 hover:bg-slate-50 hover:border-blue-200 text-slate-400 hover:text-blue-500  active:scale-[0.99] disabled:opacity-50"
                                                >
                                                    {uploading ? (
                                                        <div className="w-8 h-8 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
                                                    ) : (
                                                        <Upload className="w-6 h-6" />
                                                    )}
                                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                        {uploading ? 'Uploading...' : 'Upload Floor Plan'}
                                                    </span>
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-bold text-slate-500 tracking-wider">ลำดับการแสดงผล</label>
                                            <div className="flex items-center gap-1 bg-slate-50 p-0.5 rounded-lg border border-slate-200 h-[34px]">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newVal = Math.max(0, (newZone.display_order || 0) - 1);
                                                        setNewZone({ ...newZone, display_order: newVal });
                                                    }}
                                                    className="w-7 h-7 flex items-center justify-center rounded-md bg-white border border-slate-100 text-slate-400 hover:text-rose-500 hover:border-rose-100 hover:shadow-sm  active:scale-90"
                                                >
                                                    <Minus size={12} strokeWidth={3} />
                                                </button>

                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="flex-1 min-w-0 bg-transparent text-center text-[13px] font-bold text-slate-700 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                    value={newZone.display_order}
                                                    onChange={(e) => {
                                                        const val = parseInt(e.target.value);
                                                        if (!isNaN(val)) {
                                                            setNewZone({ ...newZone, display_order: Math.max(0, val) });
                                                        } else if (e.target.value === '') {
                                                            setNewZone({ ...newZone, display_order: 0 });
                                                        }
                                                    }}
                                                />

                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newVal = (newZone.display_order || 0) + 1;
                                                        setNewZone({ ...newZone, display_order: newVal });
                                                    }}
                                                    className="w-7 h-7 flex items-center justify-center rounded-md bg-white border border-slate-100 text-slate-400 hover:text-blue-600 hover:border-blue-100 hover:shadow-sm  active:scale-90"
                                                >
                                                    <Plus size={12} strokeWidth={3} />
                                                </button>
                                            </div>
                                        </div>


                                        <div className="space-y-1">
                                            <label className="text-[10px] font-bold text-slate-500 tracking-wider">สถานะ</label>
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                                                    className={`w-full bg-white border rounded-lg shadow-sm flex items-center px-3 py-1.5 gap-2 text-left group outline-none  ${isStatusDropdownOpen ? '' : ''}`}
                                                >
                                                    <span className={`text-[13px] font-medium truncate flex-1 ${(newZone.is_active === 1 || newZone.is_active === true) ? 'text-blue-600' : 'text-slate-500'}`}>
                                                        {(newZone.is_active === 1 || newZone.is_active === true) ? 'Active' : 'Inactive'}
                                                    </span>
                                                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isStatusDropdownOpen ? 'rotate-180 text-blue-500' : 'text-slate-400'}`} />
                                                </button>
                                                {isStatusDropdownOpen && (
                                                    <>
                                                        <div className="fixed inset-0 z-40" onClick={() => setIsStatusDropdownOpen(false)} />
                                                        <div className="absolute bottom-full mb-2 min-w-full w-max bg-white border border-slate-100 rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-200">
                                                            <div className="py-1">
                                                                {[
                                                                    { label: 'Active', value: true },
                                                                    { label: 'Inactive', value: false }
                                                                ].map(opt => (
                                                                    <button
                                                                        key={opt.label}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setNewZone({ ...newZone, is_active: opt.value });
                                                                            setIsStatusDropdownOpen(false);
                                                                        }}
                                                                        className={`w-full text-left px-4 py-2.5 text-sm font-bold flex items-center justify-between transition-colors ${(newZone.is_active === 1 || newZone.is_active === true) === opt.value ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                                                    >
                                                                        <span>{opt.label}</span>
                                                                        {(newZone.is_active === 1 || newZone.is_active === true) === opt.value && <Check className="w-4 h-4 text-blue-600" />}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="mx-5 mb-4 p-3 bg-rose-50 border border-rose-100 rounded-xl flex items-center gap-3 text-rose-600 animate-in slide-in-from-bottom-2 duration-200 shrink-0">
                                    <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                                        <AlertTriangle size={18} strokeWidth={2.5} />
                                    </div>
                                    <span className="text-xs leading-tight font-medium">{error}</span>
                                </div>
                            )}

                            <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex gap-3 shrink-0">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="flex-1 px-4 py-2.5 rounded-xl text-slate-600 text-sm font-bold hover:bg-slate-100 border border-slate-200 active:scale-95 "
                                >
                                    ยกเลิก
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-500/25 active:scale-95 "
                                >
                                    {editingZone ? 'บันทึกแก้ไข' : 'บันทึก'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}


            {showSuccessPopup && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 text-center">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setShowSuccessPopup(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[320px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center">
                            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-6 shadow-inner">
                                <Check className="w-8 h-8 text-emerald-500" />
                            </div>
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">ทำรายการสำเร็จ</h3>
                            <p className="text-[13px] font-bold text-slate-400 mt-2 leading-relaxed">{successMessage}</p>
                            <button
                                onClick={() => setShowSuccessPopup(false)}
                                className="mt-8 w-full py-3 bg-blue-600 text-white rounded-xl font-black text-sm shadow-lg shadow-blue-500/20 active:scale-95 "
                            >
                                ตกลง
                            </button>
                        </div>
                    </div>
                </div>
            )}



            {showErrorPopup && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 text-center">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setShowErrorPopup(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[340px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center">
                            <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mb-6 shadow-inner">
                                <AlertCircle className="w-8 h-8 text-rose-500" />
                            </div>
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">ไม่สามารถทำรายการได้</h3>
                            <p className="text-[13px] font-bold text-slate-400 mt-2 leading-relaxed whitespace-pre-wrap">{errorMessage}</p>
                            <button
                                onClick={() => setShowErrorPopup(false)}
                                className="mt-8 w-full py-3 bg-rose-500 text-white rounded-xl font-black text-sm shadow-lg shadow-rose-200 active:scale-95 "
                            >
                                เข้าใจแล้ว
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteConfirm && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setShowDeleteConfirm(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[340px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mb-6">
                                <Trash2 className="w-10 h-10 text-rose-500" />
                            </div>
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">ยืนยันการลบข้อมูล?</h3>
                            <p className="text-[13px] text-slate-400 mt-2">ข้อมูลนี้จะถูกลบถาวร ไม่สามารถกู้คืนได้</p>
                            <div className="flex gap-3 w-full mt-10">
                                <button
                                    onClick={() => setShowDeleteConfirm(false)}
                                    className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm active:scale-95 hover:bg-slate-200 "
                                >
                                    ยกเลิก
                                </button>
                                <button
                                    onClick={handleDelete}
                                    className="flex-1 py-2.5 bg-rose-500 text-white rounded-2xl font-black text-[14px] shadow-lg shadow-rose-200 active:scale-95 hover:bg-rose-600 tracking-wide "
                                >
                                    ลบข้อมูล
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Preview Modal */}
            {previewZone && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/90 animate-in fade-in duration-300"
                        onClick={() => setPreviewZone(null)}
                    />
                    <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col items-center">
                        <div className="flex justify-between items-center w-full px-4 py-3 border-b border-slate-100 bg-white">
                            <span className="text-sm font-bold text-slate-700 flex items-center gap-2">
                                <ImageIcon size={16} className="text-blue-500" />
                                แผนผังโซน: {previewZone.zone_name}
                            </span>
                            <button
                                onClick={() => setPreviewZone(null)}
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-2 bg-slate-100 overflow-auto max-h-[80vh] w-full flex items-center justify-center">
                            <img
                                src={`${API_URL}${previewZone.image_url}`}
                                alt={`Layout map for ${previewZone.zone_name}`}
                                className="max-w-full h-auto rounded-lg shadow-lg"
                                onError={(e) => { e.target.src = 'https://placehold.co/600x400?text=Image+Not+Found'; }}
                            />
                        </div>
                        <div className="w-full px-4 py-3 bg-white border-t border-slate-100 flex justify-end">
                            <a
                                href={`${API_URL}${previewZone.image_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] font-bold text-blue-600 hover:underline flex items-center gap-1"
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                เปิดในแท็บใหม่
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ZoneManagement;
